import { useState, useEffect } from "react";
import ConsumerHeader from "@/components/ConsumerHeader";
import ProviderBrandedHeader from "@/components/ProviderBrandedHeader";
import CacheManager from "@/components/CacheManager";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useBooking } from "@/hooks/useBooking";
import { useAccessCodes, useCreateAccessCode } from "@/hooks/useAccessCodes";
import { useDevices } from "@/hooks/useDevices";
import { useCheckOut } from "@/hooks/useCheckOut";
import { useBookingCancellation } from "@/hooks/useBookingCancellation";
import { useBookingReschedule } from "@/hooks/useBookingReschedule";
import { useProviderById } from "@/hooks/useProviders";
import { useBookingPaymentDetails } from "@/hooks/useBookingPaymentDetails";
import { useBookingReview } from "@/hooks/useBookingReview";
import { RescheduleDialog } from "@/components/booking/RescheduleDialog";
import { BookingHeader } from "@/components/booking/BookingHeader";
import { BookingServiceOverview } from "@/components/booking/BookingServiceOverview";
import { BookingActionButtons } from "@/components/booking/BookingActionButtons";
import { BookingAccessDetails } from "@/components/booking/BookingAccessDetails";
import { BookingPaymentInfo } from "@/components/booking/BookingPaymentInfo";
import { BookingHelpCard } from "@/components/booking/BookingHelpCard";
import { BookingStatusBadge } from "@/components/booking/BookingStatusBadge";
import AddReviewDialog from "@/components/dashboard/AddReviewDialog";
import { useBookingTimeCalculations } from "@/hooks/useBookingTimeCalculations";
import { useBookingCheckInLogic } from "@/hooks/useBookingCheckInLogic";
import { useBookingQRCode } from "@/hooks/useBookingQRCode";
import { Calendar, Clock, MapPin } from "lucide-react";
import { format } from "date-fns";

const BookingDetails = () => {
  const { bookingId, bookingNumber } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);

  // Use bookingId if available, otherwise use bookingNumber
  const effectiveBookingId = bookingId || bookingNumber;

  // Redirect to /auth immediately if user is not signed in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth', { replace: true });
    }
  }, [authLoading, user, navigate]);

  // Only run booking query when user is authenticated
  const { data: booking, isLoading, error } = useBooking(user ? effectiveBookingId : undefined);
  const { data: provider } = useProviderById(booking?.provider_id || '');
  const { data: accessData } = useAccessCodes(effectiveBookingId);
  const { data: devices } = useDevices(booking?.provider_id);
  const { data: paymentDetails, isLoading: paymentDetailsLoading } = useBookingPaymentDetails(booking?.id);
  const createAccessCode = useCreateAccessCode();
  const checkOut = useCheckOut();
  const cancelBooking = useBookingCancellation();
  const rescheduleBooking = useBookingReschedule();
  
  // Get the check-in window from provider settings (default to 5 minutes if not set)
  const checkInWindowMinutes = provider?.check_in_window_minutes || 5;

  // Extract access codes, time slot codes, and device info from the combined response
  const accessCodes = accessData?.accessCodes || [];
  const timeSlotCodes = accessData?.timeSlotCodes || [];
  const deviceInfo = accessData?.devices || [];

  // Check if there are any enabled devices with seam_device_id
  const hasEnabledDevices = deviceInfo.some(device => device.is_enabled && device.seam_device_id);

  // Get the current active access code
  const currentAccessCode = accessCodes?.[0];

  // Calculate checked-in status using new timestamp columns
  const isCompleted = booking?.status === 'completed' || booking?.status === 'Completed';
  const isCancelled = booking?.status === 'cancelled' || booking?.status === 'Cancelled';
  const isCheckedIn = !isCompleted && !isCancelled && !!booking?.checked_in_at;
  const isCheckedOut = !!booking?.checked_out_at;

  // Use custom hooks for complex logic
  const timeCalculations = useBookingTimeCalculations({
    booking,
    currentTime,
    checkInWindowMinutes,
    isCompleted,
    isCancelled,
    isCheckedIn
  });

  const { qrCodeDataUrl } = useBookingQRCode(booking, isCancelled);

  // Calculate check-in availability
  const canCheckIn = hasEnabledDevices && 
    currentTime >= timeCalculations.checkInAvailableTime && 
    currentTime < timeCalculations.sessionEnd && 
    !isCheckedIn && !isCompleted && !isCancelled;
  
  const canCheckOut = isCheckedIn && !isCompleted;

  const { handleCheckIn } = useBookingCheckInLogic({
    booking,
    devices,
    currentAccessCode,
    timeSlotCodes,
    createAccessCode,
    hasEnabledDevices,
    canCheckIn,
    provider
  });

  // Check if user has already left a review for this booking
  const { data: existingReview } = useBookingReview(booking?.id || '');
  const hasLeftReview = !!existingReview;

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const handleContactSupport = () => {
    navigate('/support');
  };

  const handleCancelBooking = async () => {
    if (!booking) return;
    
    try {
      await cancelBooking.mutateAsync(booking.id);
    } catch (error) {
      console.error('Cancellation failed:', error);
    }
  };

  const handleRescheduleBooking = () => {
    setRescheduleDialogOpen(true);
  };

  const handleConfirmReschedule = async (newDate: string, newStartTime: string, newEndTime: string, timeSlotId?: string) => {
    if (!booking) return;
    
    try {
      await rescheduleBooking.mutateAsync({
        bookingId: booking.id,
        newDate,
        newStartTime,
        newEndTime,
        timeSlotId
      });
      setRescheduleDialogOpen(false);
    } catch (error) {
      console.error('Reschedule failed:', error);
    }
  };

  const handleCheckOut = async () => {
    if (!booking) return;
    
    try {
      await checkOut.mutateAsync(booking.id);
    } catch (error) {
      console.error('Check-out failed:', error);
    }
  };

  // Check if booking was created by provider
  const isProviderCreated = booking?.notes && (
    booking.notes.includes('Booking created by provider') ||
    booking.notes.includes('provider will need to sign up') ||
    booking.notes.includes('Customer will need to sign up')
  );

  // Provider branding
  const primaryColor = provider?.brand_primary_color || undefined;
  const secondaryColor = provider?.brand_secondary_color || undefined;
  const hasBranding = !!primaryColor;

  // Format booking date for display
  const formatBookingDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'EEEE, d MMMM yyyy');
    } catch {
      return dateStr;
    }
  };

  // Format time for display
  const formatTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(':');
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes));
      return format(date, 'h:mm a');
    } catch {
      return time;
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <ConsumerHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Redirecting to /auth — render nothing
  if (!user) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <ConsumerHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading booking details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    const isAuthError = errorMessage.includes('Please sign in') || errorMessage.includes('not authenticated');
    
    return (
      <div className="min-h-screen bg-background">
        <ConsumerHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center max-w-md mx-auto">
            {isAuthError ? (
              <>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign In Required</h2>
                <p className="text-gray-600 mb-4">
                  Please sign in to view your booking details.
                </p>
                <button 
                  onClick={() => navigate('/auth')}
                  className="bg-gray-900 text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Sign In
                </button>
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Booking Not Found</h2>
                <p className="text-gray-600 mb-4">
                  The booking you're looking for doesn't exist or you don't have access to it.
                </p>
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-gray-900 text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Go to Dashboard
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {provider ? (
        <ProviderBrandedHeader provider={provider} />
      ) : (
        <ConsumerHeader />
      )}
      <CacheManager forceCheck={false} showNotification={false} />
      
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Quick Info Bar */}
        <div 
          className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b"
          style={{ borderColor: hasBranding ? `${primaryColor}20` : 'hsl(var(--border))' }}
        >
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <span 
                className="font-mono font-semibold"
                style={{ color: primaryColor || 'hsl(var(--foreground))' }}
              >
                #{booking.booking_number}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" style={{ color: primaryColor }} />
              <span>{formatBookingDate(booking.booking_date)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" style={{ color: primaryColor }} />
              <span>{formatTime(booking.start_time)} - {formatTime(booking.end_time)}</span>
            </div>
            {provider && (
              <div className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4" style={{ color: primaryColor }} />
                <span>{provider.location}</span>
              </div>
            )}
          </div>
          <BookingStatusBadge
            status={booking.status}
            isCompleted={isCompleted}
            isCancelled={isCancelled}
            isCheckedIn={isCheckedIn}
          />
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          <BookingServiceOverview
            booking={booking}
            statusBadge={null}
            actualCheckInTime={timeCalculations.actualCheckInTime}
            actualCheckOutTime={timeCalculations.actualCheckOutTime}
            isCheckedIn={isCheckedIn}
            isCompleted={isCompleted}
          />
          
          <BookingActionButtons
            canCheckIn={canCheckIn}
            canCheckOut={canCheckOut}
            canReschedule={timeCalculations.canReschedule}
            isCompleted={isCompleted}
            isCancelled={isCancelled}
            isCheckedIn={isCheckedIn}
            sessionHasEnded={timeCalculations.sessionHasEnded}
            hasEnabledDevices={hasEnabledDevices}
            checkInWindowMinutes={checkInWindowMinutes}
            createAccessCodePending={createAccessCode.isPending}
            checkOutPending={checkOut.isPending}
            reschedulePending={rescheduleBooking.isPending}
            onCheckIn={handleCheckIn}
            onCheckOut={handleCheckOut}
            onReschedule={handleRescheduleBooking}
            brandColor={primaryColor}
          />

          {/* Access Details & Payment in a grid on larger screens */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" id="access-details-section">
            <BookingAccessDetails
              hasEnabledDevices={hasEnabledDevices}
              isCheckedIn={isCheckedIn}
              isCompleted={isCompleted}
              isCancelled={isCancelled}
              currentAccessCode={currentAccessCode}
              createAccessCodePending={createAccessCode.isPending}
              qrCodeDataUrl={qrCodeDataUrl}
            />

            <BookingPaymentInfo
              price={booking.price}
              canCancel={timeCalculations.canCancel}
              cancelPending={cancelBooking.isPending}
              onCancel={handleCancelBooking}
              isProviderCreated={isProviderCreated}
              paymentDetails={paymentDetails}
              isLoading={paymentDetailsLoading}
            />
          </div>

          {/* Review Section - Show after session is completed */}
          {isCompleted && provider && (
            <div 
              className="bg-card rounded-xl border p-6"
              style={{ borderColor: hasBranding ? `${primaryColor}20` : 'hsl(var(--border))' }}
            >
              <h3 className="text-lg font-semibold text-foreground mb-3">Share Your Experience</h3>
              <p className="text-muted-foreground mb-4">
                How was your session with {provider.name}? Your feedback helps other customers.
              </p>
              <div className="flex items-center gap-4">
                {!hasLeftReview ? (
                  <AddReviewDialog
                    bookingId={booking.id}
                    providerName={provider.name}
                    providerId={provider.id}
                    brandColor={primaryColor}
                  />
                ) : (
                  <p className="text-green-600 font-medium">✓ Thank you for leaving a review!</p>
                )}
              </div>
            </div>
          )}

          <BookingHelpCard onContactSupport={handleContactSupport} />
        </div>

        {/* Reschedule Dialog */}
        {booking && (
          <RescheduleDialog
            open={rescheduleDialogOpen}
            onOpenChange={setRescheduleDialogOpen}
            booking={booking}
            onReschedule={handleConfirmReschedule}
            isLoading={rescheduleBooking.isPending}
          />
        )}
      </main>
    </div>
  );
};

export default BookingDetails;
